import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {useIntl, FormattedMessage, defineMessage} from 'react-intl';
import {connect} from 'react-redux';

import check from './check.svg';
import dropdownCaret from './dropdown-caret.svg';
import languageIcon from '../language-selector/language-icon.svg';
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import {selectLocale} from '../../reducers/locales.js';
import useMenuNavigation from '../../hooks/use-menu-navigation';

import menuBarStyles from './menu-bar.css';
import stylesSettingsMenu from './settings-menu.css';

const languageMenuAria = defineMessage({
    id: 'gui.aria.languageMenu',
    defaultMessage: 'Language menu',
    description: 'accessibility label for language menu'
});

const allowedLocales = [
    {code: 'en', label: 'English'},
    {code: 'fr', label: 'Français'},
    {code: 'python', label: 'Python'}
];

const LanguageMenu = ({
    currentLocale,
    isRtl,
    onChangeLanguage,
    depth = 1
}) => {
    const intl = useIntl();

    const {
        menuRef,
        isExpanded,
        handleKeyDown,
        handleKeyDownOpenMenu,
        handleOnOpen,
        handleOnClose
    } = useMenuNavigation({
        depth,
        isRtl
    });

    const handleSelectLanguage = locale => {
        onChangeLanguage(locale);
        document.documentElement.lang = locale === 'python' ? 'en' : locale;
        handleOnClose();
    };

    return (
        <button
            className={classNames(menuBarStyles.menuBarItem, menuBarStyles.hoverable, {
                [menuBarStyles.active]: isExpanded()
            })}
            aria-expanded={isExpanded()}
            aria-label={intl.formatMessage(languageMenuAria)}
            onClick={handleOnOpen}
            onKeyDown={handleKeyDown}
            ref={menuRef}
        >
            <img src={languageIcon} />
            <span className={menuBarStyles.collapsibleLabel}>
                <FormattedMessage
                    defaultMessage="Language"
                    description="Language menu"
                    id="gui.menuBar.language"
                />
            </span>
            <img src={dropdownCaret} />
            <MenuBarMenu
                className={classNames(menuBarStyles.menuBarMenu)}
                open={isExpanded()}
                place={isRtl ? 'left' : 'right'}
                onRequestClose={handleOnClose}
            >
                <MenuSection>
                    {allowedLocales.map(({code, label}) => {
                        const isSelected = currentLocale === code;
                        return (
                            <MenuItem
                                key={code}
                                onClick={() => handleSelectLanguage(code)}
                                isDataMenuItem
                                onParentKeyDown={handleKeyDownOpenMenu}
                                isSelected={isSelected}
                            >
                                <img
                                    className={classNames(stylesSettingsMenu.check, {
                                        [stylesSettingsMenu.selected]: isSelected
                                    })}
                                    src={check}
                                />
                                {label}
                            </MenuItem>
                        );
                    })}
                </MenuSection>
            </MenuBarMenu>
        </button>
    );
};

LanguageMenu.propTypes = {
    currentLocale: PropTypes.string,
    isRtl: PropTypes.bool,
    onChangeLanguage: PropTypes.func,
    depth: PropTypes.number
};

const mapStateToProps = state => ({
    currentLocale: state.locales.locale,
    isRtl: state.locales.isRtl
});

const mapDispatchToProps = dispatch => ({
    onChangeLanguage: locale => {
        dispatch(selectLocale(locale));
    }
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(LanguageMenu);
